import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { isDateOnly, parseDateOnlyForUI } from "@/lib/dateOnly"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date value to MM/DD/YYYY display format
 * Handles various input formats including ISO strings, timestamps, and Date objects
 * 
 * @param value - Date string, Date object, or null/undefined
 * @returns Formatted date string as MM/DD/YYYY, or empty string if invalid
 */
export function formatDateDisplay(value: string | Date | null | undefined): string {
  if (!value) return "";
  
  try {
    // If it's already in MM/DD/YYYY format, return as-is
    if (typeof value === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
      // Ensure proper padding
      const [month, day, year] = value.split('/');
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    
    let date: Date | null;
    if (value instanceof Date) {
      date = value;
    } else if (isDateOnly(value)) {
      date = parseDateOnlyForUI(value);
    } else {
      date = new Date(value);
    }
    
    // Check if valid date
    if (!date || isNaN(date.getTime())) {
      return typeof value === 'string' ? value : "";
    }
    
    // Format as MM/DD/YYYY
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch {
    return typeof value === 'string' ? value : "";
  }
}

/**
 * Check if a value looks like a date field based on its key name
 */
export function isDateFieldKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes('date') ||
    lowerKey.includes('deadline') ||
    lowerKey.includes('expiration') ||
    lowerKey.endsWith('_at') ||
    lowerKey.endsWith('At')
  );
}
